import { z } from "zod";
import { optionalUuid } from "./_helpers/optional";
import { ExternalIdEntrySchema } from "./external-id.schema";

const proposalStatuses = [
  "draft",
  "sent",
  "viewed",
  "approved",
  "rejected",
  "expired",
] as const;

// ADV-1185 (ADR-0048): one_time = standard project/warranty line; recurring =
// service plan quoted on the proposal — materializes a service_agreement on
// opp close-won. DB CHECK on sales.proposal_items mirrors this enum.
export const proposalItemKinds = ["one_time", "recurring"] as const;
export type ProposalItemKind = (typeof proposalItemKinds)[number];

export const ProposalSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().nullable().describe("Business"),
  account_id: z.string().uuid().nullable().describe("Account"),
  customer_role_id: z.string().uuid().nullable().describe("Customer"),
  created_by_role_id: z.string().uuid().nullable().describe("Created By"),
  proposal_number: z.string().nullable().describe("Proposal Number"),
  project_type: z.string().nullable().describe("Project Type"),
  status: z.enum(proposalStatuses).describe("Status"),
  submit_date: z.string().nullable().describe("Submit Date"),
  expires_date: z.string().nullable().describe("Expires Date"),
  sent_at: z.string().nullable().describe("Sent At"),
  viewed_at: z.string().nullable().describe("Viewed At"),
  approved_at: z.string().nullable().describe("Approved At"),
  final_price: z.number().nullable().describe("Final Price"),
  project_notes: z.string().nullable().describe("Project Notes"),
  estimated_start: z.string().nullable().describe("Estimated Start"),
  estimated_duration_days: z
    .number()
    .int()
    .nullable()
    .describe("Estimated Duration Days"),
  standard_price: z.number().nullable().describe("Standard Price"),
  discount_price: z.number().nullable().describe("Discount Price"),
  specifications: z.record(z.unknown()).nullable().describe("Specifications"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  signed_contract_url: z.string().nullable().describe("Signed Contract URL"),
  signature_data: z.record(z.unknown()).nullable().describe("Signature Data"),
  version: z.number().nullable().describe("Version"),
  salesperson_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Salesperson"),
  assigned_rep_name: z.string().nullable().optional().describe("Sales Rep"),
  // ADV-1185: DB-computed via core_api.proposals_with_rep lateral aggregation
  // over sales.proposal_items. Read-only — never written from app code.
  one_time_total: z.number().nullable().optional().describe("One-Time Total"),
  recurring_total: z.number().nullable().optional().describe("Recurring Total"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type Proposal = z.infer<typeof ProposalSchema>;

export const ProposalInsertSchema = z.object({
  customer_role_id: z.string().uuid().describe("Customer"),
  // ADV-793: opportunity_id is nullable + optional so v2 create flows that
  // start without an Opportunity still validate. The column has existed on
  // sales.proposals — the service-layer read paths already use it — this
  // exposure just lets writes go through.
  opportunity_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Opportunity"),
  // ADV-793: proposal_number is optional on the insert schema. createProposal
  // derives `P-YYYYMM-NNNN` (per account+business) when omitted; the DB column
  // stays NOT NULL.
  proposal_number: z.string().optional().describe("Proposal Number"),
  project_type: z.string().optional().describe("Project Type"),
  status: z
    .enum(proposalStatuses)
    .default("draft")
    .or(z.literal("").transform(() => undefined))
    .describe("Status"),
  submit_date: z.string().optional().describe("Submit Date"),
  expires_date: z.string().optional().describe("Expires Date"),
  final_price: z.number().optional().describe("Final Price"),
  project_notes: z.string().optional().describe("Project Notes"),
  estimated_start: z.string().optional().describe("Estimated Start"),
  estimated_duration_days: z
    .number()
    .int()
    .optional()
    .describe("Estimated Duration Days"),
  standard_price: z.number().optional().describe("Standard Price"),
  discount_price: z.number().optional().describe("Discount Price"),
  specifications: z.record(z.unknown()).optional().describe("Specifications"),
  custom_fields: z.any().optional().describe("Custom Fields"),
  salesperson_user_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Salesperson"),
});

export type ProposalInsert = z.infer<typeof ProposalInsertSchema>;

export const ProposalUpdateSchema = ProposalInsertSchema.partial();

export const ProposalQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  customer_role_id: z.string().uuid().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  deal_id: z.string().uuid().optional(),
  status: z
    .union([z.enum(proposalStatuses), z.literal("submitted")])
    .optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  created_by_role_id: z.string().uuid().optional(),
});

export const ProposalItemSchema = z.object({
  id: z.string().uuid().describe("ID"),
  proposal_id: z.string().uuid().describe("Proposal"),
  product_id: z.string().uuid().nullable().describe("Product"),
  description: z.string().nullable().describe("Description"),
  quantity: z.number().nullable().describe("Quantity"),
  unit_price: z.number().nullable().describe("Unit Price"),
  is_optional: z.boolean().describe("Optional"),
  is_selected: z.boolean().describe("Selected"),
  sort_order: z.number().nullable().describe("Sort Order"),
  specifications: z.record(z.unknown()).nullable().describe("Specifications"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  // ADV-1185: discriminator. Biconditional with service_plan_id (DB CHECK).
  kind: z.enum(proposalItemKinds).describe("Kind"),
  service_plan_id: z.string().uuid().nullable().describe("Service Plan"),
  created_at: z.string().describe("Created At"),
});
export type ProposalItem = z.infer<typeof ProposalItemSchema>;

// ADV-1185: base ZodObject for insert. Kept as a plain `.object()` so call
// sites can still `.omit(...)` / `.extend(...)` from it (e.g. import.schema.ts).
// The publicly-exported `ProposalItemInsertSchema` adds a biconditional
// refinement mirroring the DB CHECK proposal_items_kind_service_plan_biconditional.
const ProposalItemInsertObject = z.object({
  proposal_id: z.string().uuid().describe("Proposal"),
  product_id: optionalUuid().describe("Product"),
  description: z.string().optional().describe("Description"),
  quantity: z.number().optional().describe("Quantity"),
  unit_price: z.number().optional().describe("Unit Price"),
  is_optional: z.boolean().default(false).describe("Optional"),
  is_selected: z.boolean().default(true).describe("Selected"),
  sort_order: z.number().optional().describe("Sort Order"),
  specifications: z.any().optional().describe("Specifications"),
  custom_fields: z.any().optional().describe("Custom Fields"),
  kind: z.enum(proposalItemKinds).default("one_time").describe("Kind"),
  service_plan_id: optionalUuid().describe("Service Plan"),
});

function proposalItemBiconditional(
  data: z.infer<typeof ProposalItemInsertObject>,
  ctx: z.RefinementCtx,
): void {
  const kind = data.kind ?? "one_time";
  if (kind === "recurring" && !data.service_plan_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["service_plan_id"],
      message: "service_plan_id is required when kind='recurring'",
    });
  }
  if (kind === "one_time" && data.service_plan_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["service_plan_id"],
      message: "service_plan_id must be null when kind='one_time'",
    });
  }
}

// ADV-1185: re-export the base object for .omit()/.extend() consumers.
export const ProposalItemInsertObjectSchema = ProposalItemInsertObject;

export const ProposalItemInsertSchema = ProposalItemInsertObject.superRefine(
  proposalItemBiconditional,
);

// The update schema drops the biconditional refinement because partial
// updates may carry only one of (kind, service_plan_id). The DB CHECK remains
// authoritative on commit.
export const ProposalItemUpdateSchema = ProposalItemInsertObject.partial();
